import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { DatePicker, Slider, theme } from "antd";
import { useTheme } from "next-themes";
import { toast } from "sonner";
import {
  ArrowRight,
  ClipboardList,
  User,
  Users,
  FolderKanban,
  Save,
  Star,
} from "lucide-react";
import { Button } from "src/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "src/components/ui/card";
import { Input } from "src/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { Skeleton } from "src/components/ui/skeleton";
import { Textarea } from "src/components/ui/textarea";
import dayjs from "dayjs";
import { BaseUrlApi, ErrorMessage } from "../../lib/api";
import EditorTextArea from "../../components/EditorTextArea";

export default function EditTask() {
  const navigate = useNavigate();
  const { bid: taskId } = useParams();

  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [selectedEntity, setSelectedEntity] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    user_id: "",
    project_id: "",
    team_id: "",
    description: "",
    date_start: "",
    date_end: "",
    progress: 0,
    status: "pending",
    review: null,
  });

  // For Ant Design DatePicker
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Fetch task data
  const fetchTaskData = async () => {
    try {
      setIsFetching(true);
      const { data } = await axios.get(`${BaseUrlApi}/tasks/${taskId}`);
      const taskData = data.data;

      // Set form data
      setFormData({
        name: taskData.name || "",
        user_id: taskData.user_id ? taskData.user_id : "",
        project_id: taskData.project_id ? taskData.project_id : "",
        team_id: taskData.team_id ? taskData.team_id : "",
        description: taskData.description || "",
        date_start: taskData.date_start || "",
        date_end: taskData.date_end || "",
        progress: +taskData.progress || 0,
        status: taskData.status || "pending",
        review: taskData.review,
      });

      // Set date pickers
      if (taskData.date_start) {
        setStartDate(dayjs(taskData.date_start));
      }
      if (taskData.date_end) {
        setEndDate(dayjs(taskData.date_end));
      }

      // Determine which entity is selected
      if (taskData.user_id) {
        setSelectedEntity("user");
      } else if (taskData.project_id) {
        setSelectedEntity("project");
      } else if (taskData.team_id) {
        setSelectedEntity("team");
      }
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("حدث خطأ !", { description: result });
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchTaskData();
    handleFetchUsers();
    handleFetchProjects();
    handleFetchTeams();
  }, [taskId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProgressChange = (value) => {
    setFormData((prev) => ({ ...prev, progress: value }));
  };

  const handleReviewChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      review: value ? Number.parseInt(value) : null,
    }));
  };

  const handleEntitySelect = (entity) => {
    setSelectedEntity(entity);
    // Reset other entity IDs
    if (entity === "user") {
      setFormData((prev) => ({ ...prev, project_id: "", team_id: "" }));
    } else if (entity === "project") {
      setFormData((prev) => ({ ...prev, user_id: "", team_id: "" }));
    } else if (entity === "team") {
      setFormData((prev) => ({ ...prev, user_id: "", project_id: "" }));
    }
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
    if (date) {
      setFormData((prev) => ({
        ...prev,
        date_start: date.format("YYYY-MM-DD HH:mm:ss"),
      }));
    }
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    if (date) {
      setFormData((prev) => ({
        ...prev,
        date_end: date.format("YYYY-MM-DD HH:mm:ss"),
      }));
    }
  };

  // Users
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [users, setUsers] = useState([]);
  const handleFetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const { data } = await axios.get(`${BaseUrlApi}/users`);
      setUsers(data.data);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("حدث خطأ !", { description: result });
    } finally {
      setLoadingUsers(false);
    }
  };

  // Projects
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projects, setProjects] = useState([]);
  const handleFetchProjects = async () => {
    try {
      setLoadingProjects(true);
      const { data } = await axios.get(`${BaseUrlApi}/projects`);
      setProjects(data.data);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("حدث خطأ !", { description: result });
    } finally {
      setLoadingProjects(false);
    }
  };

  // Teams
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [teams, setTeams] = useState([]);
  const handleFetchTeams = async () => {
    try {
      setLoadingTeams(true);
      const { data } = await axios.get(`${BaseUrlApi}/teams`);
      setTeams(data.data);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("حدث خطأ !", { description: result });
    } finally {
      setLoadingTeams(false);
    }
  };

  const [errors, setErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    try {
      await axios.put(`${BaseUrlApi}/tasks/${taskId}`, formData);
      toast("تم التحديث", {
        description: "تم تحديث المهمة بنجاح 🎉",
      });

      navigate(-1);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("خطأ", {
          description: result,
        });
      setErrors(result);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (isFetching || loadingUsers || loadingProjects || loadingTeams) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ type: "spring", duration: 2 }}
        dir="rtl"
      >
        <form>
          <div className="border-none shadow-lg overflow-hidden rounded-md">
            {/* Card header */}
            <div className="bg-gradient-to-r from-secondaryColor/20 to-transparent p-4">
              <Skeleton className="h-6 w-40" /> {/* Card Title */}
              <Skeleton className="mt-2 h-4 w-64" /> {/* Card Description */}
            </div>

            {/* Card content */}
            <div className="p-6 space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                {/* Task Name field */}
                <div className="space-y-2">
                  <Skeleton className="h-4 w-24" /> {/* Label */}
                  <Skeleton className="h-10 w-full" /> {/* Input field */}
                </div>

                {/* Status field */}
                <div className="space-y-2">
                  <Skeleton className="h-4 w-24" /> {/* Label */}
                  <Skeleton className="h-10 w-full" /> {/* Select trigger */}
                </div>

                {/* Start Date field */}
                <div className="space-y-2">
                  <Skeleton className="h-4 w-24" /> {/* Label */}
                  <Skeleton className="h-10 w-full" /> {/* DatePicker */}
                </div>

                {/* End Date field */}
                <div className="space-y-2">
                  <Skeleton className="h-4 w-24" /> {/* Label */}
                  <Skeleton className="h-10 w-full" /> {/* DatePicker */}
                </div>

                {/* Entity selection */}
                <div className="space-y-2 md:col-span-2">
                  <Skeleton className="h-4 w-24" /> {/* Label */}
                  <div className="flex gap-2">
                    <Skeleton className="h-10 w-1/3" /> {/* Button */}
                    <Skeleton className="h-10 w-1/3" /> {/* Button */}
                    <Skeleton className="h-10 w-1/3" /> {/* Button */}
                  </div>
                </div>

                {/* Entity selection field */}
                <div className="space-y-2 md:col-span-2">
                  <Skeleton className="h-4 w-24" /> {/* Label */}
                  <Skeleton className="h-10 w-full" /> {/* Select trigger */}
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Skeleton className="h-4 w-24" /> {/* Label */}
                <Skeleton className="h-32 w-full" /> {/* Textarea */}
              </div>

              {/* Progress slider */}
              <div className="space-y-4">
                <Skeleton className="h-4 w-32" /> {/* Label */}
                <Skeleton className="h-10 w-full" /> {/* Slider */}
              </div>
            </div>

            {/* Card footer with action buttons */}
            <div className="flex justify-between p-6 bg-gradient-to-r from-secondaryColor/5 to-transparent border-t border-secondaryColor/10">
              <Skeleton className="h-10 w-24" /> {/* Cancel button */}
              <Skeleton className="h-10 w-40" /> {/* Submit button */}
            </div>
          </div>
        </form>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ type: "spring", duration: 2 }}
      dir="rtl"
    >
      <form onSubmit={handleSubmit}>
        <Card className="border-none shadow-lg overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-secondaryColor/20 to-transparent">
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleCancel}
                className="ml-2"
              >
                <ArrowRight className="h-5 w-5" />
              </Button>
              <div>
                <CardTitle className="text-xl flex items-center gap-2">
                  <ClipboardList className="h-5 w-5 text-mainColor" />
                  معلومات المهمة
                </CardTitle>
                <CardDescription>تعديل معلومات المهمة</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-6 mb-6">
            <div className="grid gap-6 md:grid-cols-2">
              {/* Task Name */}
              <div className="space-y-2">
                <label htmlFor="name">اسم المهمة</label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="أدخل اسم المهمة"
                  className="focus-visible:ring-mainColor"
                  required
                />
                {errors?.name && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.name[0]}
                  </p>
                )}
              </div>

              {/* Status */}
              <div className="space-y-2">
                <label htmlFor="status">حالة المهمة</label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => handleSelectChange("status", value)}
                >
                  <SelectTrigger className="focus:ring-mainColor">
                    <SelectValue placeholder="اختر حالة المهمة" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_progress">قيد التنفيذ</SelectItem>
                    <SelectItem value="pending">قيد الانتظار</SelectItem>
                    <SelectItem value="completed">مكتمل</SelectItem>
                  </SelectContent>
                </Select>
                {errors?.status && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.status[0]}
                  </p>
                )}
              </div>

              {/* Start Date */}
              <div className="space-y-2">
                <label htmlFor="date_start">تاريخ البدء</label>
                <DatePicker
                  className="w-full rounded-md p-2"
                  showTime
                  format="YYYY-MM-DD HH:mm:ss"
                  placeholder="اختر تاريخ ووقت البدء"
                  value={startDate}
                  onChange={handleStartDateChange}
                />
                {errors?.date_start && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.date_start[0]}
                  </p>
                )}
              </div>

              {/* End Date */}
              <div className="space-y-2">
                <label htmlFor="date_end">تاريخ الانتهاء</label>
                <DatePicker
                  className="w-full rounded-md p-2"
                  showTime
                  format="YYYY-MM-DD HH:mm:ss"
                  placeholder="اختر تاريخ ووقت الانتهاء"
                  value={endDate}
                  onChange={handleEndDateChange}
                />
                {errors?.date_end && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.date_end[0]}
                  </p>
                )}
              </div>
            </div>

            {/* Entity Selection */}
            <div className="space-y-4">
              <label>تعيين المهمة إلى</label>
              <div className="grid grid-cols-3 gap-3">
                <Button
                  type="button"
                  variant={selectedEntity === "user" ? "default" : "outline"}
                  className={
                    selectedEntity === "user"
                      ? "bg-mainColor hover:bg-mainColor/90"
                      : ""
                  }
                  onClick={() => handleEntitySelect("user")}
                >
                  <User className="h-4 w-4 ml-2" />
                  مستخدم
                </Button>
                <Button
                  type="button"
                  variant={selectedEntity === "project" ? "default" : "outline"}
                  className={
                    selectedEntity === "project"
                      ? "bg-mainColor hover:bg-mainColor/90"
                      : ""
                  }
                  onClick={() => handleEntitySelect("project")}
                >
                  <FolderKanban className="h-4 w-4 ml-2" />
                  مشروع
                </Button>
                <Button
                  type="button"
                  variant={selectedEntity === "team" ? "default" : "outline"}
                  className={
                    selectedEntity === "team"
                      ? "bg-mainColor hover:bg-mainColor/90"
                      : ""
                  }
                  onClick={() => handleEntitySelect("team")}
                >
                  <Users className="h-4 w-4 ml-2" />
                  فريق
                </Button>
              </div>
              {errors?.entity && (
                <p className="text-red-500 font-semibold text-sm">
                  يرجى اختيار نوع الكيان
                </p>
              )}
            </div>

            {/* Conditional Entity Selection */}
            {selectedEntity === "user" && (
              <div className="space-y-2">
                <label htmlFor="user_id">المستخدم</label>
                <Select
                  disabled={loadingUsers}
                  value={formData.user_id}
                  onValueChange={(value) =>
                    handleSelectChange("user_id", +value)
                  }
                >
                  <SelectTrigger className="focus:ring-mainColor">
                    <SelectValue placeholder="اختر المستخدم" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors?.user_id && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.user_id[0]}
                  </p>
                )}
              </div>
            )}

            {selectedEntity === "project" && (
              <div className="space-y-2">
                <label htmlFor="project_id">المشروع</label>
                <Select
                  disabled={loadingProjects}
                  value={formData.project_id}
                  onValueChange={(value) =>
                    handleSelectChange("project_id", +value)
                  }
                >
                  <SelectTrigger className="focus:ring-mainColor">
                    <SelectValue placeholder="اختر المشروع" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors?.project_id && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.project_id[0]}
                  </p>
                )}
              </div>
            )}

            {selectedEntity === "team" && (
              <div className="space-y-2">
                <label htmlFor="team_id">الفريق</label>
                <Select
                  disabled={loadingTeams}
                  value={formData.team_id}
                  onValueChange={(value) =>
                    handleSelectChange("team_id", +value)
                  }
                >
                  <SelectTrigger className="focus:ring-mainColor">
                    <SelectValue placeholder="اختر الفريق" />
                  </SelectTrigger>
                  <SelectContent>
                    {teams.map((team) => (
                      <SelectItem key={team.id} value={team.id}>
                        {team.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors?.team_id && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.team_id[0]}
                  </p>
                )}
              </div>
            )}

            {/* Description */}
            <div className="space-y-2">
              <label htmlFor="description">وصف المهمة</label>
              {/* <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="أدخل وصف المهمة"
                className="focus-visible:ring-mainColor min-h-[100px]"
              /> */}
              <EditorTextArea form={formData} setForm={setFormData} />

              {errors?.description && (
                <p className="text-red-500 font-semibold text-sm">
                  {errors.description[0]}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-6">
              {/* Progress */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <label htmlFor="progress">
                    نسبة الإنجاز ({formData.progress}%)
                  </label>
                </div>
                <Slider
                  marks={{
                    0: "0%",
                    25: "25%",
                    50: "50%",
                    75: "75%",
                    100: {
                      style: {
                        color: "#22c55e",
                      },
                      label: <strong>100%</strong>,
                    },
                  }}
                  value={formData.progress}
                  onChange={handleProgressChange}
                />
                {errors?.progress && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.progress[0]}
                  </p>
                )}
              </div>
              {/* Review */}
              <div className="space-y-2">
                <label htmlFor="review">التقييم</label>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      onClick={() => handleReviewChange(star.toString())}
                      key={star}
                      className={`w-5 h-5 cursor-pointer ${
                        formData.review >= star
                          ? "text-yellow-400 fill-yellow-400"
                          : "text-gray-300"
                      }`}
                    />
                  ))}
                </div>
                {errors?.review && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.review[0]}
                  </p>
                )}
              </div>
            </div>
          </CardContent>

          <CardFooter className="flex justify-between p-6 bg-gradient-to-r from-secondaryColor/5 to-transparent border-t border-secondaryColor/10">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              className="hover:bg-secondaryColor/10 hover:text-mainColor"
            >
              إلغاء
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="bg-mainColor hover:bg-secondaryColor text-white"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  جاري الحفظ...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save className="h-4 w-4" />
                  تحديث المهمة
                </span>
              )}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </motion.div>
  );
}
